import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";
import CustomFunctions from "../../dependencies/custom/custom-functions";
import { useNavigate } from "react-router-dom";
import {
  changedAllocations,
  HOUSING_CATEGORIES,
  slotForCategory,
  studentsFromAssignments,
  venueOptions,
} from "./housingUtils";

const { Text } = Typography;
const listFrom = (data) => (Array.isArray(data) ? data : data?.results || []);

const BoardingAllocation = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("Dormitory");
  const [data, setData] = useState({
    venues: [],
    assigned: [],
    unassigned: [],
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const loadVersion = useRef(0);
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError("");
    try {
      const urls = [
        Settings.urls.enrollment.studentAccommodation,
        `${Settings.urls.enrollment.unassignedAccomodation}?${new URLSearchParams({ category })}`,
        ...HOUSING_CATEGORIES.map(
          (value) =>
            `${Settings.urls.enrollment.accommodation}?${new URLSearchParams({ category: value })}`,
        ),
      ];
      const responses = await Promise.all(
        urls.map((url) =>
          Utilities.fetchWithAuth("GET", url, undefined, navigate),
        ),
      );
      if (version !== loadVersion.current) return;
      const failed = responses.find((response) => !response?.ok);
      if (failed) {
        throw new Error(
          Utilities.formatErrorMessage(
            { ...failed, msg: failed.data ? undefined : failed.msg },
            "Unable to load accommodation allocations.",
          ),
        );
      }
      const [assigned, unassigned, houses, dormitories] = responses.map(
        (response) => listFrom(response.data),
      );
      setData({
        assigned: studentsFromAssignments(assigned),
        unassigned,
        venues: [...houses, ...dormitories],
      });
    } catch (error) {
      if (version === loadVersion.current) {
        setLoadError(
          error.message || "Unable to load accommodation allocations.",
        );
      }
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [category, navigate]);

  useEffect(() => {
    load();
    return () => {
      loadVersion.current += 1;
    };
  }, [load]);

  const assignedByStudent = useMemo(
    () =>
      new Map(
        data.assigned.map((student) => [String(student.studentID), student]),
      ),
    [data.assigned],
  );

  const handleAllocate = (student) => {
    const current = assignedByStudent.get(String(student.studentID)) || student;
    setSelectedStudent(current);
    setSelections({
      house: current.house?.id,
      dormitory: current.dormitory?.id,
    });
    setSaveError("");
  };

  const changes = selectedStudent
    ? changedAllocations(selectedStudent, selections)
    : [];

  const handleConfirm = async () => {
    if (savingRef.current || !changes.length || loading || loadError) return;
    const invalid = changes.some(
      ({ category: selectedCategory, accomodationID }) => {
        const option = venueOptions(
          data.venues,
          selectedCategory,
          selectedStudent[slotForCategory(selectedCategory)]?.id,
        ).find((venue) => String(venue.value) === String(accomodationID));
        return !option || option.disabled;
      },
    );
    if (invalid) {
      setSaveError("Select an available venue in each accommodation category.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    const saved = [];
    const failed = [];
    try {
      for (const change of changes) {
        let success = false;
        try {
          success = await CustomFunctions.submitAccommodation(
            "POST",
            selectedStudent,
            change.accomodationID,
            selectedStudent.studentID,
            navigate,
          );
        } catch {
          // The other slot can still be saved; retain this one for retry.
        }
        if (success) {
          saved.push(change.category);
          const venue = data.venues.find(
            (item) => String(item.id) === String(change.accomodationID),
          );
          setSelectedStudent((current) => ({
            ...current,
            [change.slot]: { id: venue.id, name: venue.name },
          }));
        } else {
          failed.push(change.category);
        }
      }
      if (failed.length) {
        setSaveError(
          `${saved.length ? `${saved.join(" and ")} saved. ` : ""}${failed.join(" and ")} could not be saved. Review the selection and retry; saved slots will be skipped.`,
        );
      } else {
        setSelectedStudent(null);
        Utilities.showNotification(
          undefined,
          "Accommodation allocations saved.",
          "text-success",
        );
      }
      await load();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const columns = [
    { title: "Student ID", dataIndex: "studentID" },
    {
      title: "Student",
      render: (_, student) =>
        [student.firstName, student.middleName, student.lastName]
          .filter(Boolean)
          .join(" "),
    },
    { title: "Class", dataIndex: "className" },
    { title: "Residency", dataIndex: "dayBoarding" },
    {
      title: "House",
      render: (_, student) => student.house?.name || "Unassigned",
    },
    {
      title: "Dormitory",
      render: (_, student) =>
        student.dormitory?.name ||
        (student.dayBoarding === "Boarding" ? "Unassigned" : "Not applicable"),
    },
    {
      title: "Action",
      render: (_, student) => (
        <Button
          onClick={() => handleAllocate(student)}
          disabled={loading || !!loadError || saving}
          icon={<i className="fas fa-project-diagram" />}
        >
          Allocate
        </Button>
      ),
    },
  ];

  const filteredStudents = (students) => {
    const query = search.trim().toLowerCase();
    return students.filter((student) =>
      [
        student.studentID,
        student.firstName,
        student.middleName,
        student.lastName,
        student.className,
        student.house?.name,
        student.dormitory?.name,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  };

  const renderTable = (students) => (
    <Table
      rowKey="studentID"
      columns={columns}
      dataSource={loading || loadError ? [] : filteredStudents(students)}
      loading={loading}
      pagination={{ defaultPageSize: 20, hideOnSinglePage: true }}
      title={CustomFunctions.tblCaption}
      footer={CustomFunctions.tblCaption}
      scroll={{ x: 850 }}
    />
  );
  const categoryVenues = data.venues.filter(
    (venue) => venue.category === category,
  );

  return (
    <div>
      {loadError && (
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={<Button onClick={load}>Retry</Button>}
          style={{ marginBottom: 16 }}
        />
      )}
      <Row gutter={[16, 24]}>
        <Col xs={24} xl={17}>
          <Card className="shadow" title="Accommodation Allocation">
            <Space wrap style={{ marginBottom: 16 }}>
              <Select
                aria-label="Accommodation category"
                value={category}
                disabled={saving}
                onChange={(value) => {
                  setLoading(true);
                  setCategory(value);
                }}
                style={{ width: 160 }}
                options={HOUSING_CATEGORIES.map((value) => ({
                  value,
                  label: value,
                }))}
              />
              <Input.Search
                aria-label="Search students"
                placeholder="Search students, class or accommodation"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ width: 320, maxWidth: "100%" }}
              />
              <Button onClick={load} disabled={loading || saving}>
                Refresh
              </Button>
            </Space>
            <Tabs
              items={[
                {
                  key: "unassigned",
                  label: `Without ${category}`,
                  children: renderTable(data.unassigned),
                },
                {
                  key: "assigned",
                  label: `${category} Assigned`,
                  children: renderTable(
                    data.assigned.filter(
                      (student) => student[slotForCategory(category)],
                    ),
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={7}>
          <Card
            className="shadow"
            title={
              category === "House" ? "Houses" : "Dormitory Capacity Overview"
            }
            loading={loading}
          >
            {!loadError && !categoryVenues.length && (
              <Empty description={`No ${category.toLowerCase()} venues`} />
            )}
            {!loadError &&
              categoryVenues.map((venue) => {
                if (category === "House") {
                  return (
                    <div key={venue.id} style={{ marginBottom: 16 }}>
                      <Text strong>{venue.name}</Text>
                    </div>
                  );
                }
                const capacity = Number(venue.capacity);
                const remaining = Number(venue.remainingSpace);
                const occupied = capacity - remaining;
                return (
                  <div key={venue.id} style={{ marginBottom: 16 }}>
                    <Text strong>{venue.name}</Text>
                    <Progress
                      percent={
                        capacity > 0
                          ? Math.max(
                              0,
                              Math.min(100, (occupied / capacity) * 100),
                            )
                          : 0
                      }
                      format={() => `${Math.max(0, occupied)}/${capacity}`}
                    />
                    <Text type="secondary">{remaining} spaces remaining</Text>
                  </div>
                );
              })}
          </Card>
        </Col>
      </Row>
      <Modal
        title={`Allocate ${selectedStudent?.firstName || ""} ${selectedStudent?.lastName || ""} — ${selectedStudent?.studentID || ""}`}
        open={!!selectedStudent}
        onOk={handleConfirm}
        onCancel={() => {
          if (!savingRef.current) setSelectedStudent(null);
        }}
        confirmLoading={saving}
        okText="Save allocations"
        okButtonProps={{ disabled: !changes.length || loading || !!loadError }}
        cancelButtonProps={{ disabled: saving }}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {loadError && (
            <Alert
              type="error"
              showIcon
              message={loadError}
              action={<Button onClick={load}>Retry loading</Button>}
            />
          )}
          {saveError && <Alert type="error" showIcon message={saveError} />}
          {HOUSING_CATEGORIES.filter(
            (value) =>
              value === "House" || selectedStudent?.dayBoarding === "Boarding",
          ).map((value) => {
            const slot = slotForCategory(value);
            return (
              <div key={value}>
                <label htmlFor={`allocation-${slot}`}>{value}</label>
                <Select
                  id={`allocation-${slot}`}
                  aria-label={value}
                  style={{ width: "100%" }}
                  placeholder={`Select ${value.toLowerCase()}`}
                  value={selections[slot]}
                  disabled={saving || loading || !!loadError}
                  showSearch
                  optionFilterProp="label"
                  options={venueOptions(
                    data.venues,
                    value,
                    selectedStudent?.[slot]?.id,
                  )}
                  onChange={(id) =>
                    setSelections((current) => ({ ...current, [slot]: id }))
                  }
                />
              </div>
            );
          })}
          <Text type="secondary">
            House and Dormitory are saved independently. Selecting another venue
            replaces only that category.
          </Text>
        </Space>
      </Modal>
    </div>
  );
};

export default BoardingAllocation;
